import { useCallback, useEffect, useMemo, useState } from "react";
import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import type { CustomerConfig, Environment } from "../types";
import { API_BASE_URL, API_SCOPE, entities } from "../flowBuilderConstants";
import { mapFoundryCustomer } from "../flowBuilderUtils";
import { toAdminApiPath } from "../../internal-access/adminApiPath";

export function useCustomers(
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
) {
  const [customers, setCustomers] = useState<CustomerConfig[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState("");

  const [selectedCustomerKey, setSelectedCustomerKey] = useState("");
  const [environment, setEnvironment] = useState<Environment>("prod");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  const account = accounts[0] || null;
  const accountId = account?.homeAccountId || "";

  const getAccessToken = useCallback(async () => {
    if (!account) {
      throw new Error("Geen ingelogde gebruiker gevonden.");
    }

    const result = await instance.acquireTokenSilent({
      account,
      scopes: [API_SCOPE],
    });

    return result.accessToken;
  }, [instance, account]);

  useEffect(() => {
    let cancelled = false;

    const loadCustomers = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError("");

        if (!API_BASE_URL) {
          throw new Error("API base URL ontbreekt. Zet VITE_API_BASE_URL in je .env.");
        }

        const token = await getAccessToken();

        const res = await fetch(`${API_BASE_URL}${toAdminApiPath("/api/foundry/customers")}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const text = await res.text();
        let data: any;

        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`Geen JSON ontvangen: ${text.slice(0, 300)}`);
        }

        if (!res.ok) {
          throw new Error(data.error || data.details || `HTTP ${res.status}: ${text}`);
        }

        if (cancelled) return;

        const rows: any[] = data.customers || data || [];
        const mappedCustomers = rows.map(mapFoundryCustomer);

        setCustomers(mappedCustomers);

        if (mappedCustomers.length > 0) {
          setSelectedCustomerKey((current) => current || mappedCustomers[0].customerKey);
          setEnvironment(mappedCustomers[0].environment);
        }
      } catch (error: any) {
        if (cancelled) return;

        console.error(error);
        setCustomers([]);
        setCustomersError(error?.message || "Customers ophalen is mislukt.");
      } finally {
        if (!cancelled) {
          setCustomersLoading(false);
        }
      }
    };

    if (!accountId) {
      setCustomersLoading(false);
      setCustomersError("Geen ingelogde gebruiker gevonden.");
      return;
    }

    loadCustomers();

    return () => {
      cancelled = true;
    };
  }, [accountId, getAccessToken]);

  const selectedCustomer = useMemo(() => {
    return (
      customers.find((customer) => customer.customerKey === selectedCustomerKey) ||
      customers.find((customer) => customer.environment === environment) ||
      customers[0] ||
      null
    );
  }, [customers, selectedCustomerKey, environment]);

  const selectedCustomerId = selectedCustomer?.customerId || "";

  const selectedEntity = useMemo(
    () =>
      selectedCustomer
        ? entities.find((item) => item.key === selectedCustomer.entity) || null
        : null,
    [selectedCustomer]
  );

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();

    return customers
      .filter((customer) => customer.environment === environment)
      .filter((customer) => {
        if (!q) return true;

        return (
          customer.customerName.toLowerCase().includes(q) ||
          customer.customerId.toLowerCase().includes(q) ||
          customer.entityCode.toLowerCase().includes(q) ||
          customer.entity.toLowerCase().includes(q)
        );
      });
  }, [customerSearch, customers, environment]);

  const selectCustomer = (customer: CustomerConfig) => {
    setSelectedCustomerKey(customer.customerKey);
    setEnvironment(customer.environment);
    setCustomerPickerOpen(false);
  };

  return {
    customers,
    customersLoading,
    customersError,
    selectedCustomerKey,
    setSelectedCustomerKey,
    selectedCustomer,
    selectedCustomerId,
    selectedEntity,
    environment,
    setEnvironment,
    customerSearch,
    setCustomerSearch,
    customerPickerOpen,
    setCustomerPickerOpen,
    filteredCustomers,
    selectCustomer,
    getAccessToken,
  };
}
